import * as React from "react";
import { createAccount, updateAccount } from "@/services/api/online-api";
import { UploadedDocument } from "@/types";
import { FormStack } from "@/components/molecules/FormStack";
import {
  recordFromAccount as serviceProviderRecordFromAccount,
  ServiceProviderFields,
  Record as ServiceProviderRecord,
} from "./ServiceProviderFields";
import AccountManagementInfoModal from "@/components/molecules/AccountManagementModal";
import { AccountFieldProps } from "./types/AccountFieldProps";
import { AccountFormProps } from "./types/AccountFormProps";
import { getAccountFieldsComponent } from "./getAccountFieldsComponent";

export const AccountForm: React.FC<AccountFormProps> = ({
  caseId,
  signature,
  serviceProvider: defaultServiceProvider,
  serviceProviders,
  properties,
  persons,
  setBusy,
  onAccountUpdated,
  account,
  uploadedFileInfo,
  createDocument,
  updateTemplate,
  savedRecord,
  saveRecord,
}) => {
  // Determine if we're adding a new account or updating an existing one
  const addingRecord = !account;

  // State for the selected service provider
  const [serviceProvider, updateServiceProvider] = React.useState<ServiceProviderRecord>(
    serviceProviderRecordFromAccount(account, defaultServiceProvider)
  );

  // Toggle function for Account Management modal visibility
  const toggleAMModal = React.useCallback(() => {
    updateServiceProvider((prevState) => ({
      ...prevState,
      showAMModal: !prevState.showAMModal,
    }));
  }, []);

  // Function to save the account (create or update)
  const saveAccount = React.useCallback(
    ({ sector, record }) => {
      // Select function either to update or create a new account
      const proc = account?.id ? updateAccount : createAccount;
      return proc({ caseId, signature, sector, record });
    },
    [account, caseId, signature]
  );

  // Props to pass to the account field component
  const accountFieldProps: AccountFieldProps = {
    saveAccount,
    properties,
    persons,
    serviceProvider,
    setBusy,
    onAccountAdded: onAccountUpdated,
    account,
    uploadedFileInfo,
    updateTemplate,
    savedRecord,
    saveRecord,
    addingRecord,
    createDocument,
  };

  // Get the appropriate account fields component based on the service provider
  const AccountFieldComponent = getAccountFieldsComponent(serviceProvider);

  return (
    <FormStack>
      {/* If no account and no default service provider, render the service provider fields */}
      {!account && !defaultServiceProvider && (
        <ServiceProviderFields
          serviceProviders={serviceProviders}
          record={serviceProvider || {}}
          errors={{}}
          update={updateServiceProvider}
        />
      )}

      {/* Render the account fields component */}
      <AccountFieldComponent {...accountFieldProps} />

      {/* Account Management Info Modal */}
      <AccountManagementInfoModal
        isDialogVisible={!!serviceProvider.showAMModal}
        setDialogVisibility={toggleAMModal}
        accountManagement={serviceProvider.accountManagement}
      />
    </FormStack>
  );
};
